import * as React from 'react';
import { observable, action } from 'mobx';

import { Omit } from 'helpers/types';
import { uniqueId } from 'helpers/uniqueId';
import Log from 'helpers/log';

export enum DialogActionType {
  cancel,
  submit,
}

export interface ModalProps {
  onClose: (operation: DialogActionType, payload?) => void;
}

export class Modal<T> {
  id = uniqueId();
  Component: React.ComponentType<T>;
  props?: Omit<T, 'onClose'>;
}

export class ModalStoreModel {
  @observable modalList: Array<Modal<any>> = [];
  @observable isLoading = false;

  private _modalPromises: Array<{
    resolve: (value) => void;
    reject: (value) => void;
  }> = [];

  @action.bound
  showModal<T extends ModalProps>(
    Component: React.ComponentType<T>,
    props?: Omit<T, 'onClose'>,
  ) {
    const modal = new Modal<T>();

    modal.Component = Component;
    modal.props = props;

    this.modalList.push(modal);

    return new Promise<{ operation: DialogActionType; payload }>(
      (resolve, reject) => this._modalPromises.push({ resolve, reject }),
    );
  }

  @action.bound
  closeModal(operation: DialogActionType = DialogActionType.cancel, payload?) {
    const promise = this._modalPromises.pop();
    const result = { operation, payload };

    if (promise) {
      if (operation === DialogActionType.cancel) {
        promise.reject(result);
      } else {
        promise.resolve(result);
      }
    } else {
      Log.info('There are no promises in the list of modals');
    }

    this.modalList.pop();
  }

  @action
  closeAllModal() {
    while (this.modalList.length) {
      this.closeModal();
    }
  }
}

export default new ModalStoreModel();
